import { Link } from "react-router-dom";
import { useClientSelection } from "../client-context";
import { useApi } from "../hooks";
import { formatDate, formatMoney } from "../api";
import { PageHeader, MetricCard } from "../components/Ui";
import ConnectionNotice from "../components/ConnectionNotice";
import TrackingCharts, { ctmDown, type TrackingSummaryData } from "../components/TrackingSummary";
import { DataTable, NoClient, RequestView, label, type RecordData } from "../components/Workflow";
import DateRangePicker, { useUrlDateRange } from "../components/DateRangePicker";
import { REPORT_PRESETS, resolveRange, rollingDays, spanDays, todayIn, type DateRange } from "../date-range";

// Rolling windows end yesterday, the nightly sync's last full day: the same
// windows as the agency Ads pages, so a client's numbers match the agency view.
// Calendar periods (this week, month to date, last month) and custom dates go
// to the server as exact days. (Month to date holds no synced spend on the 1st,
// which is why it is not the default.)
function summaryQuery(range: DateRange, today: string) {
  const days = rollingDays(range.key);
  if (days) return `period=last_${days}`;
  const { from, to } = resolveRange(range, today);
  return `period=custom&from=${from}&to=${to}`;
}
const PLATFORM: Record<string, string> = {
  google_ads: "Google Ads", lsa: "LSA", meta: "Meta", microsoft_ads: "Microsoft Ads", linkedin: "LinkedIn",
  tiktok: "TikTok", youtube: "YouTube", yelp: "Yelp", angi: "Angi", other: "Other",
};
type Summary = {
  period_start: string;
  period_end: string;
  excluded_reports: number;
  metrics: {
    ad_spend: number | null;
    ad_spend_prior_period: number | null;
    total_calls: number | null;
    answered_calls: number | null;
    completed_revenue: number | null;
    roas: number | null;
  };
  spend_by_platform: RecordData[];
  data_availability: { has_ad_spend: boolean; note: string | null };
};

const count = (v: number | null | undefined) => (v == null ? "—" : v.toLocaleString("en-US"));
const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;
/** "Aug 18": a day inside the dashboard's own year needs no year. */
const shortDate = (day: string) => formatDate(day).replace(/, \d{4}$/, "");

/**
 * What makes up the Leads tile, from the feeds this client has: "24 Google Ads calls
 * since Sep 21, 0 forms. CTM calls missing." Google's call records start on one day
 * for every client, so a window that starts earlier says when they start; a stopped
 * CTM feed's 0 is unknown, not "0 CTM calls", and is said last, on its own.
 */
function leadParts(s: TrackingSummaryData, googleSince: string | null) {
  const t = s.totals;
  const sources = s.coverage?.sources;
  // An older server without the split: forms and calls as before.
  if (t.google_calls == null && t.ctm_calls == null) return `${plural(t.forms, "form")}, ${plural(t.calls, "tracked call")}`;
  const parts: string[] = [];
  if (t.google_calls != null && (sources?.google_ads || t.google_calls)) parts.push(`${plural(t.google_calls, "Google Ads call")}${googleSince ? ` since ${shortDate(googleSince)}` : ""}`);
  const ctmMissing = t.ctm_calls != null && (sources?.ctm || t.ctm_calls) && ctmDown(s) && !t.ctm_calls;
  if (t.ctm_calls != null && (sources?.ctm || t.ctm_calls) && !ctmMissing) parts.push(plural(t.ctm_calls, "CTM call"));
  parts.push(plural(t.forms, "form"));
  return `${parts.join(", ")}${ctmMissing ? ". CTM calls missing" : ""}`;
}

/**
 * One client's period at a glance: money and leads over the same date window,
 * then spend by platform, then the lead charts when there is anything to chart.
 * Everything else about the client has its own sidebar page.
 */
export default function ClientDashboard() {
  const { clientId, client } = useClientSelection();
  const [range, setRange] = useUrlDateRange(REPORT_PRESETS, "30d", 365);
  const today = todayIn();
  const summary = useApi<Summary>(clientId ? `/api/performance/client-summary/${clientId}?${summaryQuery(range, today)}` : null);
  // Leads, jobs and revenue for exactly the spend's dates.
  const tracking = useApi<TrackingSummaryData>(clientId && summary.data ? `/api/tracking/${clientId}/summary?from=${summary.data.period_start}&to=${summary.data.period_end}` : null);
  const periodDays = summary.data ? spanDays(summary.data.period_start, summary.data.period_end) : 0;
  const m = summary.data?.metrics;
  const s = tracking.data;
  const t = s?.totals;
  const cover = s?.coverage;
  const dates = summary.data ? `${formatDate(summary.data.period_start)} to ${formatDate(summary.data.period_end)}` : "";
  const hasLeadData = !!t && (t.leads > 0 || t.jobs_booked > 0);
  const hasSpend = !!summary.data?.data_availability.has_ad_spend;
  // A job count means something only where the job export reaches these dates.
  const jobsCovered = !!t && (cover ? cover.jobs_cover_period : t.jobs_booked > 0);
  const exportEnds = cover?.jobs && summary.data && cover.jobs.last_day < summary.data.period_end ? cover.jobs.last_day : null;
  // No call feed, no Google Ads and no form source: nothing could have counted a lead.
  const tracked = !!cover && (cover.sources.google_ads || cover.sources.ctm || cover.sources.forms);
  const leadsKnown = !!t && (t.leads > 0 || !cover || tracked);
  // Revenue: the period summary's figure when a job export covers the period exactly, else the jobs in the window.
  const revenue = m?.completed_revenue ?? (t && jobsCovered ? t.jobs_revenue : null);
  const spendForRoas = (hasSpend ? m?.ad_spend : null) || t?.spend || 0;
  const roas = m?.roas ?? (revenue && spendForRoas ? Math.round((revenue / spendForRoas) * 10) / 10 : null);
  const googleSince = cover?.google_calls_since && summary.data && cover.google_calls_since > summary.data.period_start ? cover.google_calls_since : null;
  // Job data that stops before these dates (or never arrived) blanks four tiles: one line
  // above them says why and where to fix it, instead of the same words under each tile.
  const jobNote = !!t && !jobsCovered;
  const jobNoteText = !cover?.jobs
    ? "No job export yet, so booked jobs, revenue, ROAS and cost per job stay blank."
    : summary.data && cover.jobs.first_day > summary.data.period_end
      ? `Job data starts ${shortDate(cover.jobs.first_day)}, after these dates.`
      : `Job data stops ${shortDate(cover.jobs.last_day)}.`;

  return (
    <>
      <PageHeader
        title={client?.name || "Client dashboard"}
        description={clientId ? undefined : "Pick a client to see how their period is going."}
        actions={clientId ? (
          <div className="detail-header-actions">
            {/* The period is a filter: one compact pill, like every other filter. */}
            <DateRangePicker value={range} onChange={setRange} maxDays={365} label="Period" className="dash-period" />
            <Link className="button secondary" to={`/portal?client_id=${clientId}`}>Preview client portal</Link>
          </div>
        ) : undefined}
      />
      {!clientId ? <NoClient /> : (
        <RequestView request={summary}>
          {m && summary.data && (
            <>
              {/* A call or spend feed that stopped, and a job export that ends early: one box,
                  one line each, above the numbers they leave short. */}
              <ConnectionNotice
                clientId={clientId}
                feeds={["google_ads", "ctm"]}
                also={jobNote ? <>{jobNoteText}{" "}<Link to={`/tracking?client_id=${clientId}&tab=setup#conversion-upload`}>{cover?.jobs ? "Upload a newer export" : "Upload one"}</Link></> : undefined}
              />
              <section className="dash-glance" aria-label="At a glance">
                <div className="tracking-dashboard-head">
                  <div>
                    <h2>{dates}</h2>
                  </div>
                </div>
                <div className="module-metrics dash-metrics">
                  <MetricCard
                    label="Ad spend"
                    value={hasSpend ? formatMoney(m.ad_spend ?? 0) : "—"}
                    detail={!hasSpend ? "None recorded for these dates" : m.ad_spend_prior_period != null ? `${formatMoney(m.ad_spend_prior_period)} the ${periodDays} days before` : "Recorded for these dates"}
                  />
                  <MetricCard
                    label="Leads"
                    value={t ? (leadsKnown ? count(t.leads) : "—") : "—"}
                    detail={!s ? "Loading" : !leadsKnown ? "No call tracking or lead forms set up" : t!.leads || cover ? leadParts(s, googleSince) : "No forms or calls tracked yet"}
                  />
                  {/* With no job data for these dates the notice above says so once; the tiles stay quiet. */}
                  <MetricCard
                    label="Booked jobs"
                    value={t && jobsCovered ? count(t.jobs_booked) : "—"}
                    detail={!t ? "Loading" : jobNote ? undefined
                      : exportEnds ? `${count(t.jobs_completed)} completed, job export ends ${shortDate(exportEnds)}`
                      : `${count(t.jobs_completed)} completed, ${count(t.booked)} matched to a call or lead`}
                  />
                  <MetricCard
                    label="Completed revenue"
                    value={revenue == null ? "—" : formatMoney(revenue)}
                    detail={revenue == null ? (jobNote ? undefined : "No job export for these dates") : "Completed jobs, National Accounts left out"}
                  />
                </div>
                <div className="module-metrics dash-metrics">
                  <MetricCard
                    label="ROAS"
                    value={roas == null ? "—" : `${roas}×`}
                    detail={roas == null ? (jobNote ? undefined : spendForRoas ? "Needs completed revenue" : "Needs ad spend") : "Completed revenue over ad spend"}
                  />
                  <MetricCard
                    label="Cost per booked job"
                    value={t?.jobs_booked && spendForRoas ? formatMoney(Math.round((spendForRoas / t.jobs_booked) * 100) / 100) : "—"}
                    detail={t?.jobs_booked && spendForRoas ? "Ad spend over every booked job" : t?.jobs_booked ? "Needs ad spend" : jobNote ? undefined : "Needs a booked job"}
                  />
                  {/* With CTM's calls missing the share has no true total under it (it read 100%), so it shows as unknown. */}
                  <MetricCard
                    label="From a Google Ads click"
                    value={t?.google_share == null || (s && ctmDown(s)) ? "—" : `${t.google_share}%`}
                    detail={s && ctmDown(s) ? "Unknown while CTM calls are missing"
                      : googleSince ? `Google's call records start ${shortDate(googleSince)}`
                      : "Leads with a click ID or a call from a Google ad"}
                  />
                  {/* Calls the call center logged in its uploaded reports, not the tracked calls counted under Leads. */}
                  <MetricCard
                    label="Call center calls"
                    value={count(m.total_calls)}
                    detail={m.total_calls == null ? "No call center report covers these dates" : m.answered_calls != null ? `${count(m.answered_calls)} answered, from call center reports` : "From call center reports"}
                  />
                </div>
              </section>

              <section className="panel">
                <div className="panel-heading">
                  <div>
                    <h2>By platform</h2>
                  </div>
                  <Link className="button secondary" to={`/ad-spend?client_id=${clientId}`}>{hasSpend ? "Manage spend" : "Enter spend"}</Link>
                </div>
                {(summary.data.excluded_reports > 0 || (summary.data.data_availability.note && hasSpend)) && (
                  <p className="workflow-note">
                    {summary.data.excluded_reports > 0
                      ? (summary.data.excluded_reports === 1 ? "One report left out because it does not fit inside these dates. " : `${summary.data.excluded_reports} reports left out because they do not fit inside these dates. `)
                      : ""}
                    {hasSpend ? summary.data.data_availability.note || "" : ""}
                  </p>
                )}
                <DataTable
                  // The name goes in as a cell of its own: as a string the table would re-case it ("Local services Ads").
                  // Conversions are each platform's own count (calls, form fills, page goals), not the leads above.
                  rows={(summary.data.spend_by_platform || []).map(({ conversions, ...r }) => ({ ...r, platform: <span>{PLATFORM[String(r.platform)] || label(String(r.platform))}</span>, platform_conversions: conversions }))}
                  columns={["platform", "spend", "clicks", "platform_conversions"]}
                  empty="No spend recorded for these dates. The nightly Google Ads sync fills this in, or enter it by hand under Ad spend."
                />
              </section>

              {s && !hasLeadData ? (
                <section className="panel tracking-dashboard">
                  <div className="panel-heading">
                    <div>
                      <h2>Where leads came from</h2>
                    </div>
                    <Link className="button secondary" to={`/tracking?client_id=${clientId}${tracked ? "" : "&tab=setup"}`}>{tracked ? "Open tracking" : "Set up tracking"}</Link>
                  </div>
                  <p className="workflow-note">
                    {tracked
                      ? "No leads or booked jobs for these dates. Leads per day, the path from visit to completed job, and booked jobs by channel show here once there are."
                      : "Nothing tracked for this client yet. Once the call numbers, lead forms or job export are set up, this shows leads per day, the path from visit to completed job, and booked jobs by channel."}
                  </p>
                </section>
              ) : (
                <RequestView request={tracking}>
                  {s && <TrackingCharts clientId={clientId} summary={s} rangeLabel={dates} />}
                </RequestView>
              )}
            </>
          )}
        </RequestView>
      )}
    </>
  );
}
